// src/pages/Marketing/components/DemoRequestForm.js
//
// "Request a Demo" — company data + business type + country, landing as a
// DemoRequest row in the Super Admin's own queue (SubmitDemoRequestHandler,
// internal/handlers/demo_request_handler.go; DemoRequests.js is where it's
// actioned). Business type options come from the SAME public content call
// MarketingHome already made (content.business_types) — translated labels,
// fixed slugs, per this feature's own plan doc's confirmed "dropdown, never
// free text" decision. Country options come from that same call's
// content.countries (config.ListCountryOptions() server-side — the bundled
// geodata every tenant's own Country table is seeded from, read directly
// since there's no tenant yet at this point in the flow). Every other field
// label here is UI chrome, not editable marketing copy, so it's a small
// bilingual dictionary local to this component rather than another
// MarketingContentKey — English/Arabic are the two seeded languages;
// anything else falls back to English.
import { useState } from "react";
import { submitDemoRequest } from "../../../services/api";
import { useMarketingI18n } from "../MarketingI18nContext";
import BusinessTypeSelect from "./BusinessTypeSelect";
import CountrySelect from "./CountrySelect";

const STRINGS = {
  en: {
    heading: "Request a demo",
    sub: "Tell us a bit about your business — we'll set up a trial company and get back to you.",
    company_name: "Company name",
    contact_name: "Your name",
    contact_email: "Email",
    contact_phone: "Phone (optional)",
    business_type: "Business type",
    business_type_placeholder: "Choose your business type…",
    business_type_no_match: "No matching business type",
    country: "Country",
    country_placeholder: "Choose your country…",
    country_no_match: "No matching country",
    subdomain: "Preferred subdomain (optional)",
    subdomain_hint: ".posflect.com",
    message: "Anything else we should know? (optional)",
    submit: "Request my demo",
    submitting: "Sending…",
    required: "Please fill in your name, company, email, business type, and country.",
    success_title: "Thanks — we've got it!",
    success_body: "Our team will reach out shortly to set up your trial company.",
    error_generic: "Something went wrong. Please try again in a moment.",
  },
  ar: {
    heading: "اطلب تجربة مجانية",
    sub: "أخبرنا قليلًا عن نشاطك التجاري — سنجهّز لك شركة تجريبية ونتواصل معك.",
    company_name: "اسم الشركة",
    contact_name: "اسمك",
    contact_email: "البريد الإلكتروني",
    contact_phone: "رقم الهاتف (اختياري)",
    business_type: "نوع النشاط",
    business_type_placeholder: "اختر نوع نشاطك…",
    business_type_no_match: "لا يوجد نشاط مطابق",
    country: "الدولة",
    country_placeholder: "اختر دولتك…",
    country_no_match: "لا توجد دولة مطابقة",
    subdomain: "النطاق الفرعي المفضّل (اختياري)",
    subdomain_hint: ".posflect.com",
    message: "أي تفاصيل إضافية؟ (اختياري)",
    submit: "أرسل طلب التجربة",
    submitting: "جارٍ الإرسال…",
    required: "من فضلك أدخل الاسم واسم الشركة والبريد الإلكتروني ونوع النشاط والدولة.",
    success_title: "تم الاستلام، شكرًا لك!",
    success_body: "سيتواصل معك فريقنا قريبًا لتجهيز شركتك التجريبية.",
    error_generic: "حدث خطأ ما. من فضلك حاول مرة أخرى بعد قليل.",
  },
};

const emptyForm = {
  company_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  business_type: "",
  country: "",
  preferred_subdomain: "",
  message: "",
};

export default function DemoRequestForm() {
  const { languageCode, content } = useMarketingI18n();
  const t = STRINGS[languageCode] || STRINGS.en;

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Floating labels (MarketingApp.css's own ".mkt-field" rules): the label
  // floats up on focus (:focus-within, CSS-only) or once a field actually
  // has a value — the latter needs this small JS helper since a select's
  // "emptiness" isn't something :placeholder-shown can see the way an
  // input's can, so both fields share one has-value convention instead of
  // two different CSS mechanisms.
  const fieldClass = (key, extra = "") =>
    `mkt-field${extra ? " " + extra : ""}${form[key] ? " has-value" : ""}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.contact_email.trim() || !form.business_type || !form.country) {
      setError(t.required);
      return;
    }
    setSubmitting(true);
    try {
      // language_code rides along with the visible fields — not a form
      // field itself, just the marketing site's own current language
      // (MarketingI18nContext), so the welcome email the backend sends
      // right after this (SubmitDemoRequestHandler) goes out in whichever
      // language the visitor was actually browsing in.
      await submitDemoRequest({ ...form, language_code: languageCode });
      setDone(true);
    } catch (err) {
      setError(err.data?.error || err.message || t.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mkt-form-success">
        <div className="mkt-form-success-icon" aria-hidden="true">✓</div>
        <h3>{t.success_title}</h3>
        <p>{t.success_body}</p>
      </div>
    );
  }

  const businessTypes = content?.business_types || [];
  const countries = content?.countries || [];

  return (
    <form className="mkt-form" onSubmit={handleSubmit} noValidate>
      <h2 className="mkt-form-heading">{t.heading}</h2>
      <p className="mkt-form-sub">{t.sub}</p>

      <div className="mkt-form-grid">
        <label className={fieldClass("company_name")}>
          <span>{t.company_name}</span>
          <input value={form.company_name} onChange={set("company_name")} required />
        </label>
        <label className={fieldClass("contact_name")}>
          <span>{t.contact_name}</span>
          <input value={form.contact_name} onChange={set("contact_name")} required />
        </label>
        <label className={fieldClass("contact_email")}>
          <span>{t.contact_email}</span>
          <input type="email" value={form.contact_email} onChange={set("contact_email")} required />
        </label>
        <label className={fieldClass("contact_phone")}>
          <span>{t.contact_phone}</span>
          <input type="tel" value={form.contact_phone} onChange={set("contact_phone")} />
        </label>
        <label className={fieldClass("business_type", "mkt-field-combobox")}>
          <span>{t.business_type}</span>
          <BusinessTypeSelect
            value={form.business_type}
            onChange={(slug) => setForm((f) => ({ ...f, business_type: slug }))}
            options={businessTypes}
            placeholder={t.business_type_placeholder}
            noMatchLabel={t.business_type_no_match}
          />
        </label>
        <label className={fieldClass("country", "mkt-field-combobox")}>
          <span>{t.country}</span>
          <CountrySelect
            value={form.country}
            onChange={(name) => setForm((f) => ({ ...f, country: name }))}
            options={countries}
            placeholder={t.country_placeholder}
            noMatchLabel={t.country_no_match}
          />
        </label>
      </div>

      {/* mkt-field-domain (MarketingApp.css) pins the floating label above
          the physical-left edge — where the subdomain input itself always
          renders, since mkt-subdomain-input below is forced dir="ltr" — so
          in Arabic the label sits over the actual input, not over the
          ".posflect.com" suffix that ends up on the right in that always-LTR
          row. */}
      <label className={fieldClass("preferred_subdomain", "mkt-field-full mkt-field-domain")}>
        <span>{t.subdomain}</span>
        {/* dir="ltr" on the input box only — a subdomain reads left-to-right
            in every language (it's still "mycompany.posflect.com"), but the
            floating label above it (t.subdomain) is real Arabic copy and
            must stay right-to-left like the rest of the page. */}
        <div className="mkt-subdomain-input" dir="ltr">
          <input
            value={form.preferred_subdomain}
            onChange={(e) => setForm((f) => ({ ...f, preferred_subdomain: e.target.value.toLowerCase() }))}
          />
          <span className="mkt-subdomain-suffix">{t.subdomain_hint}</span>
        </div>
      </label>

      <label className={fieldClass("message", "mkt-field-full mkt-field-textarea")}>
        <span>{t.message}</span>
        <textarea rows={3} value={form.message} onChange={set("message")} />
      </label>

      {error && <div className="mkt-form-error">{error}</div>}

      <button type="submit" className="mkt-btn mkt-btn-primary mkt-form-submit" disabled={submitting}>
        {submitting ? t.submitting : t.submit}
      </button>
    </form>
  );
}
